import React, { Suspense, useState } from 'react';
import { SupporterAvatarRow, SupporterFirstGiftLink, supportersEntryPoint, lazyWithRetry } from '@charitystack/shared';
import useSupportersSummary from '../Util/useSupportersSummary';

// The modal wraps SupportersList (and its country table), so it loads on the first open,
// never with the overlay. `lazyWithRetry`, never a bare React.lazy (stale-HTML chunk 404s).
const SupportersModal = lazyWithRetry(() => import('../SupportersModal'), 'SupportersModal');

/**
 * OverlaySupporters — the supporter avatar row and the supporters modal it opens, for the
 * overlay's left pane and its mobile layout (CON-520, Paper 7CM-0 / 6PO-0). Same rule as the
 * hosted page (supportersEntryPoint): named row, count-only "N donors" row below 3 public names,
 * "Be the first to give" below 3 supporters in total; nothing on events.
 *
 * @param {Function} [onGive] - The modal footer's Give (mobile: opens the form drawer).
 * @param {HTMLElement|null} [portalContainer] - Where the modal portals to, so it stays
 *   inside the overlay's brand scope.
 */
function OverlaySupporters({
  formType,
  enableDonorList = false,
  donorListID,
  donorListType,
  donorListData,
  showAllSupportersAnonymous = false,
  brandColors,
  previewMode = false,
  onGive,
  portalContainer,
}) {
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState(false);
  const enabled = enableDonorList && formType !== 'EVENT' && Boolean(donorListID || previewMode);
  const summary = useSupportersSummary({
    donorListID,
    enabled,
    previewMode,
    donorListType,
    donorListData,
    showAllSupportersAnonymous,
  });

  if (!enabled || !summary.ready) return null;
  const entryPoint = supportersEntryPoint(summary.supporters, {
    total: summary.total,
    canGive: typeof onGive === 'function',
  });
  if (entryPoint === 'first-gift') return <SupporterFirstGiftLink onGive={onGive} />;
  if (entryPoint !== 'row' && entryPoint !== 'count-row') return null;
  // The modal opens only when the list has rows to show.
  const canOpen = summary.supporters.length > 0;
  const noun = formType === 'PRODUCT' ? 'sponsors' : 'donors';

  return (
    <>
      <SupporterAvatarRow
        supporters={summary.supporters}
        total={summary.total}
        noun={noun}
        countOnly={entryPoint === 'count-row'}
        onOpen={
          canOpen
            ? () => {
                setRequested(true);
                setOpen(true);
              }
            : undefined
        }
      />
      {requested && (
        <Suspense fallback={null}>
          <SupportersModal
            open={open}
            onOpenChange={setOpen}
            donorListID={donorListID}
            listType={summary.listType}
            formType={formType}
            total={summary.total}
            noun={noun}
            onGive={onGive}
            brandColors={brandColors}
            previewMode={previewMode}
            donorListData={donorListData}
            showAllSupportersAnonymous={showAllSupportersAnonymous}
            container={portalContainer || undefined}
          />
        </Suspense>
      )}
    </>
  );
}

export default OverlaySupporters;
